import * as React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import Select, {
  ActionMeta,
  components,
  ControlProps,
  CSSObjectWithLabel,
  OptionProps,
} from 'react-select';
import { CommonTypography } from '../styles/typography';
import { FOLDER_GROUP_LABEL } from 'app/pages/library/videos/videoList/constants';
import {
  OptionWrapper,
  DropdownArrowIcon,
  OptionIconWrapper,
  getControlStyles,
  getOptionStyles,
} from './Dropdown';
import { ALL_FOLDERS_VALUE } from 'lib/const/Folders';
import {
  MdArrowDropDown,
  MdCreateNewFolder,
  MdFolderShared,
  MdSupervisorAccount,
} from 'react-icons/md';
import { IoMdCheckmark } from 'react-icons/io';
import { getSecondaryColor } from 'lib/context/theme/utils';

type Option = {
  value: string | number;
  label: string | number;
  isFolderShared?: boolean;
  access?: boolean | string;
};

type Icon = 'folder' | 'sharedFolder' | 'accounts' | '';

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 7px 16px 0px 16px;
  gap: 12px;
`;

const Label = styled(CommonTypography).attrs({ as: 'p' })`
  font-size: 11px;
  line-height: 16px;
  font-weight: 600;
  color: ${({ theme }) => getSecondaryColor(theme, 40)};
`;

const Line = styled.div`
  flex: 1;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => getSecondaryColor(theme, 5)};
`;

const OptionChildSubTitle = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  align-self: stretch;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.neutral[80]};
  text-overflow: ellipsis;

  /* Other/Charts Label */
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px; /* 133.333% */
`;

const SharedOptionWrapper = styled.div`
  display: block;
  width: 100%;
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

const TooltipContent = styled.div`
  z-index: 99999;
  position: absolute;
  width: 100%;
  height: auto;
  display: inline-flex;
  padding: 4px 12px 6px 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  right: 0%;
  bottom: 70%;
  left: -8%;

  border-radius: 1px;
  background: ${({ theme }) => theme.colors.white[100]};

  /* Medium Shadow */
  box-shadow:
    0px 6px 12px 0px ${({ theme }) => theme.colors.black[10]};
    0px 0px 4px 0px ${({ theme }) => theme.colors.black[5]};
  background-color: ${({ theme }) => theme.colors.white[100]};
  box-shadow: 0 8px 32px 0 ${({ theme }) => theme.colors.black[10]};
  font-size: 14px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out;
`;

const TooltipTrigger = styled.div`
  display: inline-block;
  width: 100%;
  cursor: pointer;

  &:hover + ${TooltipContent} {
    opacity: 1;
    visibility: visible;
  }
`;

const TooltipTitle = styled.div`
  color: ${({ theme }) => theme.colors.black[100]};
  font-variant-numeric: lining-nums tabular-nums;

  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
`;

const TooltipSubTitle = styled.div`
  color: ${({ theme }) => theme.colors.neutral[100]};

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px; /* 133.333% */
`;

type Props = {
  value: Option;
  options: Array<Option>;
  onChange: (value: any, actionMeta?: ActionMeta<Option>) => void; // any is used as its type is unknown.
  icon?: Icon; // Added optional icon property
};

const OptionChildWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.neutral[80]};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

//generate icon if selected
const getIcon = (
  iconName: Icon | undefined,
  isSelected: boolean,
  id: string,
  value: number | string
) => {
  if (typeof iconName === 'undefined') return null;
  switch (iconName) {
    case 'folder':
      return id.includes('option-0') && value !== ALL_FOLDERS_VALUE ? (
        <div style={{ marginRight: '-6px', marginBottom: '-3px' }}>
          <MdCreateNewFolder size={16} />
        </div>
      ) : (
        isSelected && <IoMdCheckmark size={20} />
      );
    case 'sharedFolder':
      return !isSelected ? (
        <div style={{ marginRight: '-6px', marginBottom: '-3px' }}>
          <MdFolderShared size={16} />
        </div>
      ) : (
        isSelected && <IoMdCheckmark size={20} />
      );
    case 'accounts':
      return !isSelected ? (
        <div style={{ marginRight: '-6px', marginBottom: '-3px' }}>
          <MdSupervisorAccount size={16} />
        </div>
      ) : (
        isSelected && <IoMdCheckmark size={20} />
      );
    default:
      return null;
  }
};

// show different option UI if icon exist
const CustomOption = ({ children, ...props }: any) => {
  const { selectProps, isSelected, innerProps } = props;
  if (
    children === FOLDER_GROUP_LABEL.MY_FOLDERS ||
    children === FOLDER_GROUP_LABEL.SHARED_WITH_ME
  ) {
    return (
      <LabelContainer>
        <Label>{children}</Label>
        <Line />
      </LabelContainer>
    );
  }
  return (
    <components.Option {...props}>
      <TooltipWrapper>
        <TooltipTrigger>
          <SharedOptionWrapper>
            <OptionWrapper>
              <OptionChildWrapper>{children}</OptionChildWrapper>
              <OptionIconWrapper>
                {getIcon(
                  !!props.data.isFolderShared || !!props.data.access
                    ? !!props.data.access
                      ? 'accounts'
                      : 'sharedFolder'
                    : selectProps.icon,
                  isSelected,
                  innerProps.id,
                  props.data.value
                )}
              </OptionIconWrapper>
            </OptionWrapper>
            {!!props.data.sharedBy && (
              <OptionChildSubTitle>
                Shared by {props.data.sharedBy}
              </OptionChildSubTitle>
            )}
          </SharedOptionWrapper>
        </TooltipTrigger>
        {props.data.value > 0 && (
          <TooltipContent>
            <SharedOptionWrapper>
              <TooltipTitle>{children}</TooltipTitle>
              {!!props.data.sharedBy && (
                <TooltipSubTitle>
                  Shared by {props.data.sharedBy}
                </TooltipSubTitle>
              )}
            </SharedOptionWrapper>
          </TooltipContent>
        )}
      </TooltipWrapper>
    </components.Option>
  );
};

export const FolderDropdown = (props: Props) => {
  const { options, value, onChange } = props;
  const themes = useTheme();

  //common props
  const commonProps = {
    options,
    value,
    onChange,
  };

  const DropdownIndicator = (props: any) => {
    return (
      <DropdownArrowIcon menuIsOpen={props.selectProps.menuIsOpen}>
        <MdArrowDropDown size={18} opacity={0.5} />
      </DropdownArrowIcon>
    );
  };

  const CustomSingleValue = (props: any) => {
    return props.children;
  };

  //dropdown styles
  const styles = {
    container: (base: CSSObjectWithLabel) => {
      return {
        ...base,
      };
    },

    control: (
      base: CSSObjectWithLabel,
      props: ControlProps<Option, false>
    ): CSSObjectWithLabel => {
      return {
        ...base,
        boxShadow: 'none',
        borderRadius: 6,
        minHeight: '40px',
        cursor: 'pointer',
        ...getControlStyles(props, themes),
        //@ts-ignore
        '&:hover': {
          borderColor: themes.colors.secondary[100],
        },
        '&:focus-within': {
          borderColor: themes.colors.secondary[100],
        },
      };
    },
    singleValue: (base: CSSObjectWithLabel) => ({
      ...base,
      width: '95%',
    }),
    valueContainer: (base: CSSObjectWithLabel) =>
      ({
        ...base,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        flexWrap: 'nowrap',
      }) as CSSObjectWithLabel,
    input: (base: CSSObjectWithLabel) => ({
      ...base,
    }),
    menuPortal: (base: CSSObjectWithLabel) => ({
      ...base,
      zIndex: 9999,
    }),
    menuList: (base: CSSObjectWithLabel) => ({
      ...base,
      height: 'auto',
    }),
    menu: (base: CSSObjectWithLabel) => ({
      ...base,
      marginTop: 2,
      color: themes.colors.secondary[100],
    }),
    option: (
      base: CSSObjectWithLabel,
      props: OptionProps<Option, false>
    ): CSSObjectWithLabel => {
      return {
        ...base,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        cursor: 'pointer',
        transition: 'all 0.3s',
        fontWeight: 600,
        fontSize: 15,
        zIndex: 1,
        //@ts-ignore
        ...getOptionStyles(props, themes),
      };
    },
  };

  return (
    <Select
      {...commonProps}
      className='dropdown'
      placeholder='Filter by type...'
      isDisabled={false}
      aria-label='Aria label is missing'
      menuPosition='absolute'
      menuPlacement='auto'
      maxMenuHeight={220}
      menuShouldScrollIntoView={false}
      menuPortalTarget={null}
      isLoading={false}
      components={{
        DropdownIndicator: DropdownIndicator,
        IndicatorSeparator: () => null,
        Option: CustomOption,
        SingleValue: CustomSingleValue,
      }}
      styles={styles}
      isSearchable={true}
    />
  );
};
